import { CheckIcon } from '@heroicons/react/24/solid'

export interface StepConfig {
  id: string
  label: string
  shortLabel: string
}

interface StepperProps {
  steps: StepConfig[]
  currentStep: number
  completedSteps: Set<number>
  onStepClick: (index: number) => void
}

const Stepper = ({ steps, currentStep, completedSteps, onStepClick }: StepperProps) => {
  return (
    <nav aria-label="Progress">
      {/* Mobile: show current / total */}
      <div className="sm:hidden mb-4 text-sm text-gray-600 font-medium">
        Step {currentStep + 1} of {steps.length} — {steps[currentStep].label}
      </div>

      {/* Desktop: full step bar */}
      <ol className="hidden sm:flex items-center w-full">
        {steps.map((step, index) => {
          const isCompleted = completedSteps.has(index)
          const isCurrent = index === currentStep
          const isPast = index < currentStep

          return (
            <li key={step.id} className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}>
              <button
                onClick={() => onStepClick(index)}
                className="flex flex-col items-center group focus:outline-none"
                title={step.label}
              >
                {/* Circle */}
                <span className={`flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors text-xs font-semibold ${
                  isCompleted || isPast
                    ? 'border-primary-600 bg-primary-600 text-white group-hover:bg-primary-700'
                    : isCurrent
                    ? 'border-primary-600 bg-white text-primary-600'
                    : 'border-gray-300 bg-white text-gray-400 group-hover:border-gray-400'
                }`}>
                  {isCompleted ? (
                    <CheckIcon className="h-4 w-4" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </span>
                {/* Label */}
                <span className={`mt-1 text-xs font-medium whitespace-nowrap ${
                  isCurrent ? 'text-primary-600' : isPast || isCompleted ? 'text-gray-700' : 'text-gray-400'
                }`}>
                  {step.shortLabel}
                </span>
              </button>

              {/* Connector */}
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mt-[-1rem] ${
                  isPast || isCompleted ? 'bg-primary-600' : 'bg-gray-200'
                }`} />
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

export default Stepper
